'use client';

import { useEffect, useState } from 'react';
import { ListMenu, ListMenuType } from 'components/pcc-components';
import { CatHeading } from 'components/blocks-components';
import { useTranslations } from 'next-intl';
import { BlocksCheckboxChangeEvent } from 'blocks-nextjs/blocks-web-components/events';
import useFilter from 'hooks/use-filter';
import MeasureUnitSelector from './measure-unit-selector/measure-unit-selector';
import BucketModelDropdown from './bucket-model-dropdown';
import { setUnitOfMeasureCookie } from 'utils/uomCookie';
import {
  filterFacets,
  getFacetsToDelete,
  getFacetItems,
  getUpdatedFacets
} from './utils';
import { SpecificationsProps } from './types';
import { BUCKET_MODEL_NUMBER } from './constants';
import styles from './specifications.module.scss';
import cx from 'classnames';
import { updateUserPreferences } from 'services/userPreferences/action';
import { useLoaderOverlay } from 'app/[lang]/[esite]/contexts/loader-overlay-context';
import { decodeString } from '@cat-ecom/common/utils/stringUtils';

const Specifications = ({
  facets,
  defaultUom,
  locale
}: SpecificationsProps) => {
  const t = useTranslations();
  const { handleSortByFacet, handleSortByUomChange } = useFilter();

  const [filteredFacets, setFilteredFacets] = useState(
    filterFacets(facets, defaultUom)
  );
  const { selectedFacets, setSelectedFacets, startTransition } =
    useLoaderOverlay();
  const [uomSelected, setUomSelected] = useState<string>(defaultUom);
  useEffect(() => {
    setFilteredFacets(filterFacets(facets, uomSelected));
  }, [facets, uomSelected]);

  const handleItemClick = (ev: BlocksCheckboxChangeEvent) => {
    if (!selectedFacets && ev.detail.value) {
      setSelectedFacets(decodeString(ev.detail.value));
    } else if (typeof selectedFacets === 'string') {
      ev.detail.checked
        ? setSelectedFacets([selectedFacets, decodeString(ev.detail.value)])
        : setSelectedFacets(undefined);
    } else {
      const updatedFacets = selectedFacets.filter(
        (item) => item !== decodeString(ev.detail.value)
      );
      ev.detail.checked
        ? setSelectedFacets([...selectedFacets, decodeString(ev.detail.value)])
        : setSelectedFacets(updatedFacets);
    }

    startTransition(() => {
      handleSortByFacet(ev.detail.value, ev.detail.checked);
    });
  };

  const handleUomChange = (uomValue: string) => {
    setUnitOfMeasureCookie(uomValue);
    setUomSelected(uomValue);
    updateUserPreferences(uomValue);
    const facetsToDelete = getFacetsToDelete(
      facets,
      uomSelected,
      selectedFacets
    );

    if (facetsToDelete.length > 0) {
      const updatedFacets = getUpdatedFacets(
        Array.isArray(selectedFacets) ? selectedFacets : [selectedFacets],
        facetsToDelete
      );
      setSelectedFacets(updatedFacets);
      startTransition(() => {
        handleSortByUomChange(facetsToDelete);
      });
    }
  };

  const bucketModelNumber = facets.find(
    (item) => item.facetId === BUCKET_MODEL_NUMBER
  )?.value;

  return (
    <div className={cx(styles['specifications__container'])}>
      <CatHeading variant="title-sm">{t('SPECIFICATIONS')}</CatHeading>
      <div className="my-md-0 mb-3">
        <MeasureUnitSelector
          defaultUom={uomSelected}
          onChangeUom={handleUomChange}
          locale={locale}
        />
      </div>
      {bucketModelNumber && <BucketModelDropdown />}
      <div className={cx(styles['specifications__container'])}>
        {filteredFacets?.map(({ label, value }) => (
          <div
            className={cx(styles['specifications__facets-container'])}
            key={label}
          >
            <ListMenu
              headingProps={{
                className: cx(
                  {
                    [styles['specifications__facets-heading--disabled']]:
                      value.length === 0
                  },
                  styles['specifications__facets-heading--enabled'],
                  'm-0 cat-u-theme-typography-label'
                )
              }}
              headingSlot={label}
              listItems={getFacetItems(value, selectedFacets)}
              handleItemClick={handleItemClick}
              buttonViewLessText={t('VIEW_LESS_BUTTON')}
              buttonViewMoreText={t('VIEW_MORE_PRODUCTS')}
              type={ListMenuType.CHECKBOX}
              defaultItemsCount={8}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
export default Specifications;
